import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "./Logo";
import { NAV_LINKS } from "../lib/data";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const isHome = pathname === "/";
  const solid = scrolled || open;
  const linkColor = solid ? "text-navy" : "text-white";

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 z-50 w-full transition-colors duration-300 ${
        solid
          ? "bg-white/90 backdrop-blur-xl shadow-[0_8px_30px_rgb(0,0,0,0.05)]"
          : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-8">
        <Logo light={!solid} />

        <div className="hidden items-center gap-8 lg:flex">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `relative text-sm font-medium transition-colors duration-200 hover:text-brandred ${linkColor} ${
                  isActive ? "text-brandred" : ""
                }`
              }
            >
              {({ isActive }) => (
                <span className="relative">
                  {l.label}
                  {isActive && (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute -bottom-1.5 left-0 h-0.5 w-full bg-brandred"
                    />
                  )}
                </span>
              )}
            </NavLink>
          ))}
          <Link
            to="/quote"
            data-testid="nav-request-quote"
            className="inline-flex items-center gap-1.5 rounded-sm bg-brandred px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-brandred-600"
          >
            Request Quote
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          aria-label="Toggle navigation menu"
          onClick={() => setOpen((o) => !o)}
          className={`lg:hidden ${linkColor}`}
        >
          {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden bg-white lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1 px-6 pb-6 pt-2">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="border-b border-navy/5 py-3 text-base font-medium text-navy"
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/quote"
                data-testid="nav-request-quote"
                className="mt-3 inline-flex items-center justify-center gap-1.5 rounded-sm bg-brandred px-5 py-3 text-base font-semibold text-white"
              >
                Request Quote <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
